import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Workspace } from '../api/types'
import { CreateWorkspaceForm } from '../components/CreateWorkspaceForm'
import { ImportForm } from '../components/ImportForm'
import { Modal } from '../components/Modal'
import { ShowForm } from '../components/ShowForm'
import { Badge, Button, Card, EmptyState, ErrorNote, LoadingBlock, PageHeader } from '../components/ui'
import { useShows } from '../hooks/useShows'
import { useCurrentWorkspace } from '../hooks/useWorkspace'

export function HomePage() {
  const { workspace } = useCurrentWorkspace()
  if (!workspace) {
    return (
      <>
        <PageHeader title="Welcome to indiefuture" />
        <Card>
          <EmptyState
            title="Create your first workspace"
            body="A workspace holds your shows, episodes and team."
            action={<div className="narrow"><CreateWorkspaceForm /></div>}
          />
        </Card>
      </>
    )
  }
  return <Shows workspace={workspace} />
}

function Shows({ workspace }: { workspace: Workspace }) {
  const shows = useShows(workspace.id)
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const canEdit = workspace.role !== 'viewer'
  const newShow = canEdit ? (
    <>
      <Button variant="secondary" onClick={() => setImporting(true)}>Import from RSS</Button>
      <Button onClick={() => setCreating(true)}>New show</Button>
    </>
  ) : undefined
  return (
    <>
      <PageHeader
        title={workspace.name}
        subtitle={
          <span className="row">
            <Badge tone={workspace.plan === 'premium' ? 'accent' : 'neutral'}>{workspace.plan} plan</Badge>
            <span className="muted">Your shows</span>
          </span>
        }
        actions={newShow}
      />
      {shows.isPending && <LoadingBlock />}
      {shows.isError && <ErrorNote error={shows.error} />}
      {shows.data?.length === 0 && (
        <Card>
          <EmptyState
            title="Create your first show"
            body="A show holds episodes. Pick audio or video; each episode is also detected from its file."
            action={newShow}
          />
        </Card>
      )}
      {shows.data && shows.data.length > 0 && (
        <div className="show-grid">
          {shows.data.map((show) => (
            <Link key={show.id} to={`/studio/shows/${show.id}`} className="card show-card">
              <h3>{show.title}</h3>
              <p className="muted clamp-2">{show.description || 'No description yet.'}</p>
              <div className="row">
                <Badge>{show.medium}</Badge>
                <span className="muted small">
                  {show.episode_count} {show.episode_count === 1 ? 'episode' : 'episodes'}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <Modal open={importing} title="Import a show" onClose={() => setImporting(false)} wide>
        <ImportForm
          workspaceId={workspace.id}
          onDone={(summary) => {
            setImporting(false)
            navigate(`/studio/shows/${summary.show_id}`)
          }}
        />
      </Modal>
      <Modal open={creating} title="New show" onClose={() => setCreating(false)} wide>
        <ShowForm
          workspaceId={workspace.id}
          onDone={(show) => {
            setCreating(false)
            navigate(`/studio/shows/${show.id}`)
          }}
        />
      </Modal>
    </>
  )
}
